type Entity = Record<string, any>

const isTranslationMap = (value: unknown): value is Record<string, string> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/**
 * Makes sure each `<field>_translations` map exists on an entity loaded from the API,
 * so the multilingual inputs can bind to it. The API always sends the maps, a missing
 * (or array-encoded empty) one becomes an empty object. The entity is updated in place
 * so the reactive store object keeps its identity.
 */
export function withNameTranslations<T extends Entity | null | undefined>(
  entity: T,
  fields: string[] = ['name']
): T {
  if (!entity) {
    return entity
  }
  const target: Entity = entity
  for (const field of fields) {
    const key = `${field}_translations`
    if (!isTranslationMap(target[key])) {
      target[key] = {}
    }
  }
  return entity
}

/**
 * Builds the payload expected by the API: each translatable field is sent as a
 * `{ locale: string }` object and the read-only `<field>_translations` companion is dropped.
 */
export function toTranslatedNamePayload(
  entity: Entity,
  fields: string[] = ['name']
): Entity {
  const payload = { ...entity }
  for (const field of fields) {
    const key = `${field}_translations`
    if (isTranslationMap(payload[key])) {
      payload[field] = { ...payload[key] }
    }
    delete payload[key]
  }
  return payload
}

/**
 * Name to display for an entity being edited: the value typed in the given locale
 * (kept in `<field>_translations`, which is what the form edits) with the plain string
 * as fallback. Used for page titles that must follow the form without a refetch.
 */
export function localizedEntityName(
  entity: Entity | null | undefined,
  locale: string,
  field = 'name'
): string {
  const translations = entity?.[`${field}_translations`]
  const translated = isTranslationMap(translations) ? translations[locale] : ''
  return translated || entity?.[field] || ''
}
